import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import AdminShell from "@/components/admin/AdminShell";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Shield, AlertTriangle, CheckCircle2, XCircle, Plus, Trash2, RotateCw, Zap, ExternalLink, Lock, ShieldCheck } from "lucide-react";

/**
 * Owner-only: paste / rotate / test the platform-direct provider keys
 * (OPENAI_API_KEY, GEMINI_API_KEY, etc.). The plaintext only ever travels
 *   browser → backend HTTPS → encrypted in Mongo (Fernet)
 * and is never echoed back to the client.
 *
 * For maximum security the founder is advised to paste directly into
 * `backend/.env` instead — that path is documented in the page header.
 */

const FEATURE_USAGE = {
  openai:     "Sora 2 video, GPT-5.2 text generation, Whisper, OpenAI TTS voiceover",
  gemini:     "Nano Banana image generation, Gemini 3 text generation",
  anthropic:  "Claude Sonnet / Opus text generation",
  fal:        "Future: Flux / Veo 3 / Runway routing",
  elevenlabs: "ElevenLabs v3 voiceover + Eleven Music (commercial music & SFX)",
};

export default function OwnerPlatformKeysPage() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [editing, setEditing] = useState(null); // provider id when editing
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [secret, setSecret] = useState("");
  const [label, setLabel] = useState("");
  const [routing, setRouting] = useState(null);
  const [bufferRec, setBufferRec] = useState(null);

  const refresh = async () => {
    try { const { data } = await api.get("/admin/platform-keys"); setItems(data.keys || []); }
    catch (e) { setMsg({ kind: "error", text: formatApiErrorDetail(e.response?.data?.detail) || e.message }); }
    try { const { data } = await api.get("/health/billing-routing"); setRouting(data); } catch {}
    try { const { data } = await api.get("/health/buffer-recommendation"); setBufferRec(data); } catch {}
  };

  useEffect(() => { if (user?.role === "owner") refresh(); }, [user]);

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login?next=/owner/platform-keys" replace />;
  if (user.role !== "owner") return <Navigate to="/dashboard" replace />;

  const onSave = async (providerId) => {
    setBusy(providerId); setMsg(null);
    try {
      const { data } = await api.put("/admin/platform-keys", { provider: providerId, secret, label });
      const t = data.test || {};
      setMsg({
        kind: t.ok === true ? "success" : t.ok === false ? "error" : "info",
        text: t.message || `${providerId} key saved.`,
      });
      setEditing(null); setSecret(""); setLabel("");
      await refresh();
    } catch (e) {
      setMsg({ kind: "error", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusy(null); }
  };

  const onTest = async (providerId) => {
    setBusy(providerId); setMsg(null);
    try {
      const { data } = await api.post(`/admin/platform-keys/${providerId}/test`);
      setMsg({
        kind: data.ok === true ? "success" : data.ok === false ? "error" : "info",
        text: data.message,
      });
      await refresh();
    } catch (e) {
      setMsg({ kind: "error", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusy(null); }
  };

  const onDelete = async (providerId) => {
    setBusy(providerId); setMsg(null);
    try {
      await api.delete(`/admin/platform-keys/${providerId}`);
      setMsg({ kind: "success", text: `${providerId} platform key removed. Falling back to env / Emergent UK.` });
      setConfirmDelete(null);
      await refresh();
    } catch (e) {
      setMsg({ kind: "error", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusy(null); }
  };

  return (
    <AdminShell
      title={{ eyebrow: "Owner · Platform Keys", heading: "Platform-Direct Provider Keys" }}
      subtitle="Paste the YBM Inc OpenAI / Google / Anthropic / fal.ai / ElevenLabs keys here. Keys are encrypted at rest with Fernet (AES-128-GCM); the plaintext only lives in memory inside the router."
      breadcrumbs={[{ label: "Owner", to: "/owner" }, { label: "Platform Keys" }]}
    >
      <SecurityCallout />

      {bufferRec && <BufferRecommendation rec={bufferRec} />}
      {routing && <BillingRoutingTable routing={routing} />}

      {msg && (
        <div
          data-testid="platform-keys-msg"
          className={`mb-6 px-4 py-3 border text-sm flex items-start gap-3 ${
            msg.kind === "success" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" :
            msg.kind === "error"   ? "border-red-500/40 bg-red-500/10 text-red-300" :
                                     "border-amber-500/40 bg-amber-500/10 text-amber-300"
          }`}
        >
          {msg.kind === "success" ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> :
           msg.kind === "error"   ? <XCircle className="w-4 h-4 mt-0.5" /> :
                                    <AlertTriangle className="w-4 h-4 mt-0.5" />}
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it) => (
          <PlatformKeyCard
            key={it.provider}
            item={it}
            isEditing={editing === it.provider}
            isConfirmingDelete={confirmDelete === it.provider}
            isBusy={busy === it.provider}
            secret={secret}
            label={label}
            onSecret={setSecret}
            onLabel={setLabel}
            onStartEdit={() => { setEditing(it.provider); setSecret(""); setLabel(""); }}
            onCancelEdit={() => { setEditing(null); setSecret(""); setLabel(""); }}
            onSave={() => onSave(it.provider)}
            onTest={() => onTest(it.provider)}
            onAskDelete={() => setConfirmDelete(it.provider)}
            onCancelDelete={() => setConfirmDelete(null)}
            onDelete={() => onDelete(it.provider)}
          />
        ))}
      </div>
    </AdminShell>
  );
}

function BufferRecommendation({ rec }) {
  return (
    <div className="mb-6 border border-[#2A2A2A] bg-[#121212] p-6" data-testid="uk-buffer-rec">
      <p className="text-xs uppercase tracking-[0.22em] text-[#A0A0A0] mb-4">Universal Key · recommended buffer</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-4">
        <Stat label="Last 30d UK spend"     value={`$${(rec.uk_spend_last_30d_usd || 0).toFixed(2)}`} />
        <Stat label="Daily avg on UK"        value={`$${(rec.daily_avg_usd || 0).toFixed(2)}`} />
        <Stat label="UK calls (30d)"         value={String(rec.uk_calls_last_30d || 0)} />
        <Stat label="Recommended buffer"     value={`$${(rec.recommended_buffer_usd || 0).toFixed(2)}`} highlight />
      </div>
      <p className="text-[11px] text-[#A0A0A0] leading-relaxed">
        <span className="text-[#D4AF37] font-bold">Basis:</span> {rec.recommended_buffer_basis}
      </p>
      <p className="text-[11px] text-[#5A5A5A] leading-relaxed mt-2">{rec.note}</p>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">{label}</div>
      <div className={`font-display text-2xl font-extrabold ${highlight ? "text-[#D4AF37]" : "text-[#F4F4F0]"}`}>{value}</div>
    </div>
  );
}

function BillingRoutingTable({ routing }) {
  return (
    <div className="mb-8 border border-[#2A2A2A] bg-[#0F0F0F]" data-testid="billing-routing-table">
      <div className="px-5 py-4 border-b border-[#2A2A2A] flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs uppercase tracking-[0.22em] text-[#A0A0A0]">Billing & Routing · who gets billed for what</p>
        <span className="text-[10px] text-emerald-300 uppercase tracking-wider">
          ✓ {routing.summary?.your_keys_active?.length || 0} key{routing.summary?.your_keys_active?.length === 1 ? "" : "s"} active
        </span>
      </div>
      <div className="divide-y divide-[#1A1A1A]">
        {routing.features.map((f, i) => (
          <div key={i} className="px-5 py-4 flex flex-col md:flex-row md:items-start gap-3" data-testid={`route-row-${i}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#F4F4F0] font-bold">{f.feature}</p>
              <p className="text-[11px] text-[#A0A0A0] mt-1 leading-relaxed">{f.note}</p>
            </div>
            <div className="text-left md:text-right whitespace-nowrap">
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border ${
                  f.locked_to_emergent
                    ? "border-amber-500/40 text-amber-300"
                    : f.billed_to_label === "Universal Key" || f.billed_to_label === "Not configured"
                    ? "border-blue-500/40 text-blue-300"
                    : "border-emerald-500/40 text-emerald-300"
                }`}
              >
                {f.locked_to_emergent && <Lock className="w-3 h-3" />}
                {f.billed_to_label}
              </span>
              <p className="text-[10px] text-[#5A5A5A] mt-1 font-mono">{f.current}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="px-5 py-3 border-t border-[#2A2A2A] bg-[#0A0A0A] text-[11px] text-[#A0A0A0] flex items-start gap-2">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 mt-0.5 flex-shrink-0" />
        <span><b className="text-[#F4F4F0]">No double-charging:</b> {routing.summary?.double_charge_explanation}</span>
      </div>
    </div>
  );
}

function SecurityCallout() {
  return (
    <div className="mb-8 border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6" data-testid="platform-keys-security-callout">
      <div className="flex items-start gap-3">
        <Shield className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
        <div className="space-y-3 text-sm">
          <p className="font-display text-base font-bold text-[#F4F4F0]">
            Most secure: paste into <code className="bg-[#0A0A0A] px-2 py-0.5 text-[#D4AF37] text-xs">backend/.env</code>
          </p>
          <p className="text-[#A0A0A0] leading-relaxed">
            For the lowest attack surface, edit <code className="bg-[#0A0A0A] px-1.5 text-[#F4F4F0] text-xs">/app/backend/.env</code>,
            replace <code className="bg-[#0A0A0A] px-1.5 text-[#F4F4F0] text-xs">OPENAI_API_KEY=&quot;&quot;</code> with your
            real key, then run <code className="bg-[#0A0A0A] px-1.5 text-[#F4F4F0] text-xs">sudo supervisorctl restart backend</code>.
            The key never touches the database, the browser, or any log line.
          </p>
          <p className="text-[#A0A0A0] leading-relaxed">
            The form below is a convenience layer. Keys saved here are encrypted with Fernet (AES-128-GCM) using a master key
            stored in <code className="bg-[#0A0A0A] px-1.5 text-[#F4F4F0] text-xs">PROVIDER_KEY_ENCRYPTION_KEY</code>. DB-stored
            keys take priority over env keys, so saving a key here transparently overrides whatever is in <code className="bg-[#0A0A0A] px-1.5 text-[#F4F4F0] text-xs">.env</code>.
          </p>
          <p className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-bold">
            Lookup order: user vault → platform DB → platform env → Emergent Universal Key
          </p>
        </div>
      </div>
    </div>
  );
}

function PlatformKeyCard({
  item, isEditing, isConfirmingDelete, isBusy, secret, label,
  onSecret, onLabel, onStartEdit, onCancelEdit, onSave, onTest,
  onAskDelete, onCancelDelete, onDelete,
}) {
  const source = item.effective_source; // 'db' | 'env' | null
  const sourceBadge =
    source === "db"  ? { label: "Active · DB",  color: "border-emerald-500/40 text-emerald-300" } :
    source === "env" ? { label: "Active · .env", color: "border-[#D4AF37]/40 text-[#D4AF37]" } :
                       { label: "Not configured", color: "border-[#2A2A2A] text-[#5A5A5A]" };
  const lastTestPill =
    item.db_last_test_ok === true  ? <span className="text-[10px] text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Valid</span> :
    item.db_last_test_ok === false ? <span className="text-[10px] text-red-300 inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Failed</span> :
                                     null;

  return (
    <div
      className={`border p-5 transition-colors ${source ? "border-emerald-500/30 bg-[#0F0F0F]" : "border-[#2A2A2A] bg-[#121212]"}`}
      data-testid={`platform-key-card-${item.provider}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-display text-xl font-bold text-[#F4F4F0]">{item.label}</div>
          <code className="text-[10px] text-[#5A5A5A] font-mono">{item.env_name}</code>
        </div>
        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border ${sourceBadge.color}`}>
          {sourceBadge.label}
        </span>
      </div>

      <p className="text-xs text-[#A0A0A0] mb-3 leading-relaxed">
        <span className="text-[#D4AF37] font-semibold">Powers:</span> {FEATURE_USAGE[item.provider] || "—"}
      </p>

      {item.db_has_value && !isEditing && !isConfirmingDelete && (
        <div className="space-y-2 mb-3">
          <p className="text-xs text-[#A0A0A0]">
            DB key: <span className="font-mono text-[#F4F4F0]">{item.db_preview}</span>
          </p>
          {item.db_label && <p className="text-xs text-[#A0A0A0]">Label: {item.db_label}</p>}
          <p className="text-[10px] text-[#5A5A5A]">
            Updated {(item.db_updated_at || "").slice(0, 16).replace("T", " ")}
            {item.db_last_tested_at && (
              <>  ·  tested {(item.db_last_tested_at || "").slice(0, 16).replace("T", " ")}  ·  {lastTestPill}</>
            )}
          </p>
        </div>
      )}

      {item.env_has_value && !item.db_has_value && (
        <p className="text-[10px] text-[#D4AF37] mb-3">● Reading from environment variable</p>
      )}

      {isEditing ? (
        <div className="space-y-3">
          <input
            type="password"
            value={secret}
            onChange={(e) => onSecret(e.target.value)}
            placeholder={`Paste your ${item.label} secret key`}
            data-testid={`platform-secret-${item.provider}`}
            className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-sm font-mono text-[#F4F4F0] placeholder-[#5A5A5A]"
          />
          <input
            type="text"
            value={label}
            onChange={(e) => onLabel(e.target.value)}
            placeholder={`Label (e.g. YBM Inc ${item.label})`}
            data-testid={`platform-label-${item.provider}`}
            className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-xs text-[#F4F4F0] placeholder-[#5A5A5A]"
          />
          <div className="flex gap-2">
            <button
              onClick={onSave}
              disabled={isBusy || secret.length < 8}
              data-testid={`platform-save-${item.provider}`}
              className="bg-[#D4AF37] text-[#080808] font-bold px-4 py-2 text-xs hover:bg-[#F0C84B] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              <Lock className="w-3 h-3" /> Encrypt & Save
            </button>
            <button
              onClick={onCancelEdit}
              className="px-4 py-2 text-xs text-[#A0A0A0] hover:text-[#F4F4F0]"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : isConfirmingDelete ? (
        <div className="p-3 border border-red-500/40 bg-red-500/10 mt-2" data-testid={`platform-confirm-delete-${item.provider}`}>
          <p className="text-xs text-red-200 mb-3">Remove this DB key? The router will fall back to env / Emergent UK.</p>
          <div className="flex gap-2">
            <button
              onClick={onDelete}
              disabled={isBusy}
              data-testid={`platform-confirm-delete-yes-${item.provider}`}
              className="bg-red-500/80 hover:bg-red-500 text-white text-xs font-bold px-3 py-1.5 transition-colors disabled:opacity-50"
            >
              Yes, remove
            </button>
            <button
              onClick={onCancelDelete}
              className="text-xs text-[#A0A0A0] hover:text-[#F4F4F0] px-3 py-1.5"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {item.db_has_value ? (
            <>
              <button
                onClick={onStartEdit}
                data-testid={`platform-rotate-${item.provider}`}
                className="inline-flex items-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37] px-3 py-1.5 text-xs transition-colors"
              >
                <RotateCw className="w-3 h-3" /> Rotate / Replace
              </button>
              <button
                onClick={onTest}
                disabled={isBusy}
                data-testid={`platform-test-${item.provider}`}
                className="inline-flex items-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37] px-3 py-1.5 text-xs transition-colors disabled:opacity-50"
              >
                <Zap className="w-3 h-3" /> Test
              </button>
              <button
                onClick={onAskDelete}
                data-testid={`platform-delete-${item.provider}`}
                className="inline-flex items-center gap-2 text-xs text-red-300 hover:text-red-200 transition-colors ml-auto"
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </>
          ) : (
            <button
              onClick={onStartEdit}
              data-testid={`platform-add-${item.provider}`}
              className="inline-flex items-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37] px-3 py-1.5 text-xs transition-colors"
            >
              <Plus className="w-3 h-3" /> Add Key
            </button>
          )}
        </div>
      )}
    </div>
  );
}
